import { Component, EventEmitter, HostListener, inject, Input, OnInit, Output, signal } from '@angular/core';
import { ButtonModule } from 'primeng/button';
import { CustomFilter, DynamicColumn, TableComponent } from '../../../../@shared/components/table-component/table-component';
import { Car } from '../../../../models/car';
import { FormControl } from '@angular/forms';
import { Router } from '@angular/router';
import { PaginatorState } from 'primeng/paginator';
import { ModelService } from '../../../../services/vehicles/model-service';
import { enumToOptions } from '../../../../@shared/utility/enumToOptions';
import { CarStatus, UserRole } from '../../../../models/enums';
import { UpdateCarForm } from '../../../../@shared/forms/vehicles/cars/update-car-form/update-car-form';
import { DeleteCarForm } from '../../../../@shared/forms/vehicles/cars/delete-car-form/delete-car-form';
import { Model } from '../../../../models/model';
import { CancelReservationForm } from '../../../../@shared/forms/cars/cancel-reservation-form/cancel-reservation-form';
import { Employee } from '../../../../models/employee';
import { EmployeeService } from '../../../../services/employees/employee-service';
import { UpdateCarStatusForm } from '../../../../@shared/forms/vehicles/cars/update-car-status-form/update-car-status-form';
import { CarsListCards } from '../cars-list-cards/cars-list-cards';
import { CarsListMap } from '../cars-list-map/cars-list-map';
import { CarsListMobile } from '../cars-list-mobile/cars-list-mobile';
import { Card } from 'primeng/card';

@Component({
  selector: 'app-cars-list',
  imports: [TableComponent, ButtonModule, UpdateCarForm, DeleteCarForm, CancelReservationForm, UpdateCarStatusForm, CarsListCards, CarsListMap, CarsListMobile,Card],
  templateUrl: './cars-list.html',
  styleUrl: './cars-list.scss'
})
export class CarsList implements OnInit {


  @Input() cars: Car[] = [];
  @Input() totalRecords = 0;

  @Input() rows = 20;
  @Input() first = 0;

  @Input() searchControl!: FormControl;

  @Input() modelsFilter: CustomFilter;

  @Output() pageChange = new EventEmitter<PaginatorState>();
  @Output() searchChange = new EventEmitter<void>();
  @Output() reloadCars = new EventEmitter<void>();
  @Output() viewChange = new EventEmitter<'list' | 'cards' | 'map'>();

  private timeAgo(dateStr: string | undefined): string {
    if (!dateStr) return '---';
    const diff = Date.now() - new Date(dateStr).getTime();
    const totalMins = Math.floor(diff / 60000);
    const days = Math.floor(totalMins / 1440);
    const hours = Math.floor((totalMins % 1440) / 60);
    const mins = totalMins % 60;
    const parts: string[] = [];
    if (days > 0) parts.push(`${days}d`);
    if (hours > 0) parts.push(`${hours}h`);
    if (mins > 0 || parts.length === 0) parts.push(`${mins}min`);
    return parts.join(' ');
  }

  tableColumns: DynamicColumn<Car>[] = [
    {
      header: 'PLATE',
      field: 'plateNo',
      sortable: true,
      isPlateCell: true,
    },
    {
      header: 'MODEL',
      field: 'model.maker',
      sortField: 'iotModel',
      sortable: true,
      valueGetter: (row) => `${row.model?.maker ?? ''} ${row.model?.name ?? ''}`.trim(),
      bottomField: 'model.bodyType',
    },
    {
      header: 'FUEL',
      field: 'telemetry.fuelLevel',
      sortable: true,
      sortField: 'fuelLevel',
      isProgressBar: true,
      isFilterable: true,
      filterType: 'slider',
      min: 0,
      max: 100,
      step: 1,
      progressColorGetter: (value) => {
        if (value >= 70) return 'var(--dark-green-background)';
        if (value >= 30) return 'var(--background-progress-bar)';
        return 'var(--status-discharged-text)';
      }
    },
    {
      header: 'BATTERY',
      field: 'telemetry.powerSupplyVoltage',
      sortable: true,
      sortField: 'powerSupplyVoltage',
      isProgressBar: true,
      valueGetter: (row) => {
        if (!row.telemetry?.powerSupplyVoltage) return 0;
        return Math.min((row.telemetry.powerSupplyVoltage / 12) * 100, 100).toFixed(0);
      },
      progressColorGetter: (value) => {
        if (value >= 70) return 'var(--dark-green-background)';
        if (value >= 30) return 'var(--background-progress-bar)';
        return 'var(--status-discharged-text)';
      }
    },
    {
      header: 'STATUS LONG',
      field: 'statusDurationSeconds',
      sortable: false,
      cellClass: 'text-center',
      isDuration: true,
      durationFormat: 'dhm',
    },
    {
      header: 'LAST RIDE',
      field: 'lastRideAt',
      sortable: true,
      cellClass: 'text-center',
      valueGetter: (row) => this.timeAgo(row.lastRideAt),
    },
    {
      header: 'LAST UPDATE',
      field: 'telemetry.updatedAt',
      sortable: true,
      sortField: 'updatedAt',
      cellClass: 'text-center',
      locationCell: true,
      valueGetter: (row) => this.timeAgo(row.telemetry?.updatedAt),
      command: (car) => {
        const lat = car.telemetry?.position?.latitude;
        const lng = car.telemetry?.position?.longitude;
        if (lat != null && lng != null) {
          window.open(`https://www.google.com/maps/search/?api=1&query=${lat},${lng}`, '_blank');
        }
      },
    },
    {
      header: 'Status',
      field: 'status',
      sortable: true,
      isStatusBadge: true,
      isFilterable: true,
      filterType: 'multiselect',
      filterOptions: enumToOptions(CarStatus),
      command: (car) => this.openUpdateCarStatusDialog(car),
      badgeStyleGetter: (value) => {
        const map: Record<string, { background: string; color: string }> = {
          'AVAILABLE': { background: 'var(--status-available-bg)', color: 'var(--status-available-text)' },
          'IN_USE': { background: 'var(--status-in-use-bg)', color: 'var(--status-in-use-text)' },
          'RESERVED': { background: 'var(--status-reserved-bg)', color: 'var(--status-reserved-text)' },
          'MAINTENANCE': { background: 'var(--status-maintenance-bg)', color: 'var(--status-maintenance-text)' },
          'DISCHARGED': { background: 'var(--status-discharged-bg)', color: 'var(--status-discharged-text)' },
          'REBALANCING': { background: 'var(--status-rebalancing-bg)', color: 'var(--status-rebalancing-text)' },
          'NEEDS_INVESTIGATION': { background: 'var(--status-investigate-bg)', color: 'var(--status-investigate-text)' },
          'TRANSPORTATION': { background: 'var(--status-transportation-bg)', color: 'var(--status-transportation-text)' },
          'INTERNAL_RENT': { background: 'var(--status-internal-rent-bg)', color: 'var(--status-internal-rent-text)' },
          'NOT_READY': { background: 'var(--status-not-ready-bg)', color: 'var(--status-not-ready-text)' },
          'CLEANING': { background: 'var(--status-cleaning-bg)', color: 'var(--status-cleaning-text)' },
          'NEEDS_CLEANING': { background: 'var(--status-need-cleaning-bg)', color: 'var(--status-need-cleaning-text)' },
          'STOLEN': { background: 'var(--status-stolen-bg)', color: 'var(--status-stolen-text)' },
          'TOTAL_LOSS': { background: 'var(--status-total-loss-bg)', color: 'var(--status-total-loss-text)' },
          'OPS_IN_USE': { background: 'var(--status-ops-use-bg)', color: 'var(--status-ops-use-text)' },

        };
        return map[value as string] ?? { background: 'var(--status-reserved-bg)', color: 'var(--status-reserved-text)' };
      }
    },
  ];

  activeView = signal<'list' | 'cards' | 'map'>('list');
  isMobile = signal(window.innerWidth <= 768);

  @HostListener('window:resize')
  onResize() {
    this.isMobile.set(window.innerWidth <= 768);
  }

  setView(view: 'list' | 'cards' | 'map') {
    this.activeView.set(view);
    this.viewChange.emit(view);
  }

  employee = signal<Employee | undefined>(undefined);
  UserRole = UserRole

  selectedCar: Car
  addCarDialog = false
  updateCarDialog = false
  updateCarStatusDialog = false
  deleteCarDialog = false
  cancelReservationDialog = false

  rentalTypeFilter: CustomFilter = {
    key: "rentalType",
    label: "Rental Type",
    type: "chip",
    options: [
      { label: 'Sharing', value: 'CAR_SHARING' },
      { label: 'Rental', value: 'CAR_RENTAL' },
    ]
  }

  needsRebalancingFilter: CustomFilter = {
    key: "needsRebalancing",
    label: "Needs Rebalancing",
    shortLabel: "Rebalancing",
    type: "select",
    options: [
      { label: 'YES', value: "true" },
      { label: 'NO', value: "false" },
    ]
  }


  private modelService = inject(ModelService)
  private employeeService = inject(EmployeeService)
  private router = inject(Router)

  ngOnInit() {
    this.getModels()
    this.getEmployee()
  }

  getEmployee = () => {
    this.employee = this.employeeService.employee;
  }

  getModels() {
    this.modelService.getModelsForDropdown().subscribe({
      next: (res) => {
        const options = res.data.map((m: Model) => ({ label: m.name, value: m.id }));
        this.modelsFilter.options = options;
      }
    });
  }

  openAddCarDialog = () => {
    this.addCarDialog = true
  }

  onCarAdded = () => {
    this.reloadCars.emit();
    this.addCarDialog = false
  }

  openUpdateCarDialog = (selectedRow: Car) => {
    this.selectedCar = selectedRow
    this.updateCarDialog = true
  }

  onCarUpdated = () => {
    this.reloadCars.emit();
    this.updateCarDialog = false;
  };

  openUpdateCarStatusDialog = (selectedRow: Car) => {
    this.selectedCar = selectedRow
    this.updateCarStatusDialog = true
  }

  onCarStatusUpdated = () => {
    this.reloadCars.emit();
    this.updateCarStatusDialog = false;
  };

  openDeleteCarDialog = (selectedRow: Car) => {
    this.selectedCar = selectedRow
    this.deleteCarDialog = true
  }

  onCarDeleted = () => {
    this.reloadCars.emit();
    this.deleteCarDialog = false
  }

  openCancelReservationDialog = (selectedRow: Car) => {
    this.selectedCar = selectedRow
    this.cancelReservationDialog = true
  }

  onCarCanceled = () => {
    this.reloadCars.emit();
    this.cancelReservationDialog = false
  }

  redirectToCarDetails = (car: Car) => {
    this.router.navigate([`vehicles/${car.id}`])
  }

  carRowLink = (car: Car) => `/vehicles/${car.id}`;


}
